import { useContext, useState, useEffect } from 'react'
import { UserContext } from '../../../../context/UserContextProvider'
import { apiGet, apiPut, getBackendUrl } from '../../Api_Functions'
import { clearUnsavedChanges } from '../../utilities/unsavedChangesGuard'
import { clearAssistantConversation } from '../../utilities/assistant_conversation_utils'
import { clearVisionMark } from '../../../vision/visionMark'

/**
 * @typedef {('VALID'|'INVALID'|'EXPIRED'|'NOT_FOUND'|'NO_CONNECTION'|'WRONG_SIGNATURE'|'INVALID_DATE'|'MAX_MACHINE_REACH'|'MISMATCH_LICENSE')} LicenseStatus License status
 * @returns {{user: import('../../utilities/objectStructures/user/User').User, changeUserLanguage:function(string), userLogin:function(), userLogout:function()}} return object
 */
const useUser = () => {
  const userContext = useContext(UserContext)
  const [toggle, setToggle] = useState(false)
  /** @type {[LicenseStatus, React.Dispatch<SetStateAction<LicenseStatus>>]} */
  const [licenseStatus, setLicenseStatus] = useState('VALID')

  useEffect(() => {
    const handleStorageChange = () => {
      if (!window.localStorage.getItem('jwt') && userContext.user) {
        clearUnsavedChanges()
        userContext.setUser(null)
        if (window.location.pathname !== '/login') {
          window.location.href = '/login'
        }
      }
    }
    const interval = setInterval(handleStorageChange, 1000)
    return () => clearInterval(interval)
  }, [userContext])

  useEffect(() => {
    if (userContext.user) return
    fetch(getBackendUrl() + 'token/isTokenValid')
      .then(response => {
        if (response.status !== 200) {
          if (response.status === 403) {
            response.json().then(data => {
              if (data && data.message) {
                setLicenseStatus(data.message)
              }
            })
          }
          if (response.status >= 500 && response.status < 600) {
            setLicenseStatus(null)
          }
          userContext.setUser(null)
          window.localStorage.removeItem('mic-session')
          window.localStorage.removeItem('jwt')
          return
        }
        apiGet('user/details')
          .then(user => {
            userContext.setUser(user)
          })
          .catch(() => {
            userContext.setUser(null)
            window.localStorage.removeItem('jwt')
          })
      })
      .catch((error) => {
        setLicenseStatus(null)
        console.error('Error fetching user details:', error)
        userContext.setUser(null)
        window.localStorage.removeItem('jwt')
        window.localStorage.removeItem('mic-session')
      })
  }
  , [userContext, toggle])
  if (!userContext) {
    throw new Error('useUser must be used within a UserProvider')
  }

  const userLogin = () => {
    clearUnsavedChanges()
    window.localStorage.removeItem('jwt')
    setToggle(prev => !prev)
  }

  const userLogout = async () => {
    await apiPut('token/logout')
    userContext.setUser(null)
    window.localStorage.removeItem('jwt')
    window.localStorage.removeItem('mic-session')
    clearAssistantConversation()
    // Story 213 (C1 step 3): the camera vigilance of this session ends with it.
    clearVisionMark()
  }

  const changeUserLanguage = language => {
    const newUser = { ...userContext.user, userConfiguration: { ...userContext.user.userConfiguration, languageCode: language } }
    userContext.setUser(newUser)
    return newUser
  }

  return {
    user: userContext.user,
    changeUserLanguage,
    userLogin,
    userLogout,
    getLicenseStatus: licenseStatus
  }
}

export default useUser
